import React, { useState } from 'react';
import Chatbot from 'react-chatbot-kit';
import 'react-chatbot-kit/build/main.css';
import { MessageCircle, X, Minimize2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import config from './ChatbotConfig';
import MessageParser from './MessageParser';
import ActionProvider from './ActionProvider';
import './chatbot.styles.css';

const ChatbotWidget = ({ courseCode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Store courseCode globally for ActionProvider to access
  React.useEffect(() => {
    window.__CHATBOT_COURSE_CODE__ = courseCode;
  }, [courseCode]);

  const toggleChatbot = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setUnreadCount(0); // Reset unread count when opening
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      <motion.button
        onClick={toggleChatbot}
        className="fixed bottom-6 right-6 w-14 h-14 bg-[#429ccd] hover:bg-[#1a425f] dark:bg-[#429ccd] dark:hover:bg-[#1a425f] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center z-50"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <>
            <img
              src="/botlogo.jpg"
              alt="Chat Bot"
              className="w-10 h-10 rounded-full object-cover"
            />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </>
        )}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-6 w-[350px] h-[500px] bg-white dark:bg-gray-800 rounded-lg shadow-2xl z-50 flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700"
          >
            {/* Header */}
            <div className="bg-[#429ccd] dark:bg-[#1a425f] text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img
                  src="/botlogo.jpg"
                  alt="Bot Logo"
                  className="w-8 h-8 rounded-full object-cover"
                />
                <h3 className="font-semibold">Learning Assistant</h3>
              </div>
              <button
                onClick={toggleChatbot}
                className="hover:bg-[#1a425f] dark:hover:bg-[#429ccd] rounded-full p-1 transition-colors"
              >
                <Minimize2 className="w-5 h-5" />
              </button>
            </div>

            {/* Chatbot Component */}
            <div className="flex-1 overflow-hidden chatbot-container">
              <Chatbot
                config={config}
                messageParser={MessageParser}
                actionProvider={ActionProvider}
                placeholderText="Type your question here..."
                headerText="Learning Assistant"
                disableScrollToBottom={false}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatbotWidget;
